import {
  ConfirmDialog,
  deletePerson,
  dropAccountInvite,
  getAccounts,
  InvalidInput,
  invitePerson,
  languages,
  MoreIcon,
  Popover,
  resetPerson,
  setPerson,
  signOutPerson,
  type AccountsPage,
  type Person,
} from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { problemKey } from '../../lib/format';

const locale = (language: string) => (language === 'et' ? 'et-EE' : 'en-GB');

/** `18 Oct 2026` from an instant. */
function dayOf(at: string, language: string): string {
  return new Intl.DateTimeFormat(locale(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(at));
}

/** What an admin may do to another account, in a menu. */
function Actions({
  person,
  onChange,
  onSignOut,
  onReset,
  onDelete,
}: {
  person: Person;
  onChange: (change: { admin?: boolean; disabled?: boolean }) => void;
  onSignOut: () => void;
  onReset: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const item = (label: string, act: () => void) => (
    <button
      type="button"
      role="menuitem"
      className="data-menu-item"
      onClick={() => {
        setOpen(false);
        act();
      }}
    >
      {label}
    </button>
  );
  const label = t('people.actions', { name: person.name });
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      label={label}
      trigger={(props) => (
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={label}
          title={label}
          {...props}
        >
          <MoreIcon />
        </button>
      )}
    >
      <div className="data-menu" role="menu">
        {item(t(person.admin ? 'people.unmakeAdmin' : 'people.makeAdmin'), () => {
          onChange({ admin: !person.admin });
        })}
        {item(t(person.disabledAt === null ? 'people.turnOff' : 'people.turnOn'), () => {
          onChange({ disabled: person.disabledAt === null });
        })}
        {item(t('people.signOut'), onSignOut)}
        {item(t('people.reset'), onReset)}
        {item(t('people.delete'), onDelete)}
      </div>
    </Popover>
  );
}

/** One account: who, what it is, since when and how it signs in. */
function PersonRow({
  person,
  me,
  children,
}: {
  person: Person;
  me: boolean;
  children: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tags = (
    [
      ['you', me],
      ['first', person.first],
      ['admin', person.admin && !person.first],
      ['off', person.disabledAt !== null],
    ] as const
  )
    .filter(([, shown]) => shown)
    .map(([key]) => key);
  const facts = [
    t('people.since', { day: dayOf(person.createdAt, lang) }),
    person.lastSeenAt === null
      ? t('people.neverSeen')
      : t('people.lastSeen', { day: dayOf(person.lastSeenAt, lang) }),
    t('people.trips', { count: person.trips }),
    ...(person.twoStep ? [t('people.twoStep')] : []),
    ...(person.passkeys > 0 ? [t('people.passkeys', { count: person.passkeys })] : []),
    ...(person.assistants > 0 ? [t('people.assistants', { count: person.assistants })] : []),
  ];
  return (
    <li className={person.disabledAt === null ? 'people-row' : 'people-row people-off'}>
      <div className="people-who">
        <b>{person.name}</b>
        {tags.map((key) => (
          <span key={key} className={`people-tag people-tag-${key}`}>
            {t(`people.${key}`)}
          </span>
        ))}
        <span className="quiet">{person.email}</span>
        <small className="quiet">{facts.join(' · ')}</small>
      </div>
      {children}
    </li>
  );
}

/** The invitation form: an address, the mail's language. */
function InviteForm({ onSent }: { onSent: (page: AccountsPage, email: string) => void }) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState(i18n.language === 'et' ? 'et' : 'en');
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      className="people-invite"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        invitePerson(email, language).then(
          (page) => {
            setEmail('');
            onSent(page, email.trim().toLowerCase());
          },
          (problem: unknown) => {
            setError(
              problem instanceof InvalidInput
                ? problem.fields.map((field) => t(problemKey(field.problem))).join(' ')
                : t('people.failed'),
            );
          },
        );
      }}
    >
      <div className="field">
        <label className="field-label" htmlFor={`${ids}-email`}>
          {t('people.email')}
        </label>
        <input
          id={`${ids}-email`}
          className="text-input"
          type="email"
          value={email}
          aria-invalid={error !== null}
          aria-describedby={error === null ? undefined : `${ids}-error`}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
        {error !== null && (
          <p className="field-error" id={`${ids}-error`}>
            {error}
          </p>
        )}
      </div>
      <div className="segmented segmented-small" role="group" aria-label={t('people.language')}>
        {languages.map((one) => (
          <button
            key={one.code}
            type="button"
            lang={one.code}
            aria-pressed={language === one.code}
            onClick={() => {
              setLanguage(one.code);
            }}
          >
            {one.name}
          </button>
        ))}
      </div>
      <button type="submit" className="button button-primary">
        {t('people.send')}
      </button>
    </form>
  );
}

/** The instance's accounts for its admins: listed, changed, invited. */
export function Users() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; page: AccountsPage }
  >({ kind: 'loading' });
  const [said, setSaid] = useState<{ text: string; link: string | null } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Person | null>(null);
  useEffect(() => {
    getAccounts().then(
      (page) => {
        setState({ kind: 'ready', page });
      },
      () => {
        setState({ kind: 'failed' });
      },
    );
  }, []);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind === 'failed') {
    return (
      <p className="page form-failed" role="alert">
        {t('people.loadFailed')}
      </p>
    );
  }
  const { page } = state;
  const shown = (next: AccountsPage) => {
    setState({ kind: 'ready', page: next });
  };
  const act = (job: Promise<AccountsPage>) => {
    setFailed(null);
    setSaid(null);
    job.then(shown, (problem: unknown) => {
      setFailed(t(problem instanceof InvalidInput ? 'people.shared' : 'people.failed'));
    });
  };
  const lang = i18n.language;
  return (
    <div className="page people-page">
      <header>
        <h1>{t('people.title')}</h1>
        <p className="quiet">{t('people.lead')}</p>
      </header>
      {failed !== null && (
        <p className="form-failed" role="alert">
          {failed}
        </p>
      )}
      {said !== null && (
        <div className="people-said" role="status">
          <span>{said.text}</span>
          {said.link !== null && <code>{said.link}</code>}
        </div>
      )}
      <div className="people-layout">
        <section className="panel" aria-labelledby="people-accounts">
          <div className="panel-head">
            <h2 id="people-accounts">{t('people.count', { count: page.users.length })}</h2>
          </div>
          <ul className="people-list">
            {page.users.map((person) => (
              <PersonRow key={person.id} person={person} me={person.id === page.me}>
                {person.id !== page.me && !person.first && (
                  <Actions
                    person={person}
                    onChange={(change) => {
                      act(setPerson(person.id, change));
                    }}
                    onSignOut={() => {
                      act(signOutPerson(person.id));
                    }}
                    onReset={() => {
                      setFailed(null);
                      resetPerson(person.id).then(
                        (sent) => {
                          setSaid(
                            sent.mail
                              ? {
                                  text: t('people.resetSent', { email: person.email }),
                                  link: null,
                                }
                              : {
                                  text: t('people.resetLink', { email: person.email }),
                                  link: sent.link,
                                },
                          );
                        },
                        () => {
                          setFailed(t('people.failed'));
                        },
                      );
                    }}
                    onDelete={() => {
                      setDeleting(person);
                    }}
                  />
                )}
              </PersonRow>
            ))}
          </ul>
        </section>
        <div className="people-side">
          <section className="panel" aria-labelledby="people-invite">
            <div className="panel-head">
              <h2 id="people-invite">{t('people.invite')}</h2>
            </div>
            <p className="quiet">{t('people.inviteHow')}</p>
            <InviteForm
              onSent={(next, email) => {
                shown(next);
                setFailed(null);
                setSaid(
                  next.sent?.mail === false
                    ? { text: t('people.sentLink', { email }), link: next.sent.link }
                    : { text: t('people.sentMail', { email }), link: null },
                );
              }}
            />
          </section>
          {page.invites.length > 0 && (
            <section className="panel" aria-labelledby="people-waiting">
              <div className="panel-head">
                <h2 id="people-waiting">
                  {t('people.waiting', { count: page.invites.length })}
                </h2>
              </div>
              <ul className="people-list">
                {page.invites.map((invite) => (
                  <li key={invite.id} className="people-row">
                    <div className="people-who">
                      <b>{invite.email}</b>
                      <small className="quiet">
                        {t('people.until', { day: dayOf(invite.expiresAt, lang) })}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="button button-small"
                      onClick={() => {
                        act(dropAccountInvite(invite.id));
                      }}
                    >
                      {t('people.takeBack')}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
      {deleting !== null && (
        <ConfirmDialog
          open
          title={t('people.deleteTitle', { name: deleting.name })}
          body={t('people.deleteBody')}
          confirmLabel={t('people.delete')}
          onConfirm={() => {
            setDeleting(null);
            act(deletePerson(deleting.id));
          }}
          onCancel={() => {
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
